import React, { useState, useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { notification, Card, Input, Button, Select, Form } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import { ArrowLeftOutlined } from '@ant-design/icons';
import posthog from 'posthog-js';
import { affiliateDailyCountCheck, affiliateRegistration } from '@/api/user';
import { businessTypes } from 'src/enums/ESmbDataCollection';
import { ProgressStepsState } from 'src/enums/ERegistrationSteps';
import { getInitialProgressSteps } from '@/utils/demoRegistrationUtils';
import SandboxerProgressIndicator from '@/components/SandboxerComponents/SandboxerProgressIndicator';
import { MOBILE_LOGO_S3_URL } from 'src/constants';
import LoginModal from '@/components/LoginModal/LoginModal';

const AffiliateRegisterPage: React.FC = () => {
	const [form] = Form.useForm();
	const [credentials, setCredentials] = useState(null);
	const [shareLink, setShareLink] = useState('');
	const [isOtherBusinessType, setIsOtherBusinessType] = useState(false);
	const [registrationStep, setRegistrationStep] = useState<'form' | 'processing' | 'success'>(
		'form',
	);
	const [progressSteps, setProgressSteps] = useState<ProgressStepsState>(getInitialProgressSteps());
	const [showLoginModal, setShowLoginModal] = useState(true);
	const [affiliateData, setAffiliateData] = useState(null);
	const [selectedReferralId, setSelectedReferralId] = useState(null);

	const registrationMutation = useMutation({
		mutationFn: async (formValues: any) => {
			const result = await affiliateRegistration(formValues, (step, status) => {
				setProgressSteps((prev) => ({
					...prev,
					[step]: status,
				}));
			});

			if (!result.success) {
				throw new Error(result.error || 'Registration failed');
			}

			return result.data;
		},
		onSuccess: (data) => {
			setCredentials({
				username: data.username,
				password: data.password,
				email: data.email,
				phone: data.phone,
			});
			setShareLink(data.shareLink);
			setRegistrationStep('success');

			notification.success({
				message: 'Registration Successful',
				description: 'Your affiliate account has been created successfully.',
			});

			posthog.capture('completed-affiliate-registration');
		},
		onError: (error) => {
			const errorMessage =
				error instanceof Error
					? error.message
					: 'Could not complete the setup at this time. Please try again later.';
			notification.error({
				message: 'Registration Failed',
				description: errorMessage,
			});
			setRegistrationStep('form');
		},
	});

	const dailyCountCheckQuery = useQuery({
		queryKey: ['affiliateDailyCount', selectedReferralId],
		queryFn: async () => {
			if (!affiliateData?.referralId || affiliateData?.referralId?.length === 0) {
				throw new Error('No referral ID selected');
			}
			const result = await affiliateDailyCountCheck(affiliateData?.referralId);
			return result;
		},
		enabled: affiliateData?.referralId && affiliateData.referralId.length > 0,
	});

	useEffect(() => {
		const RECAPTCHA_SITE_KEY = import.meta.env.VITE_APP_RECAPTCHA_SITE_KEY as string;
		if (!document.querySelector('script[src*="recaptcha"]')) {
			const script = document.createElement('script');
			script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
			script.async = true;
			document.head.appendChild(script);
		}

		posthog.capture('started-affiliate-registration');
	}, []);

	const handleSubmit = async (values: any) => {
		if (!affiliateData?.referralId || affiliateData.referralId.length === 0) {
			notification.error({ message: 'Please generate a share link first' });
			return;
		}
		setRegistrationStep('processing');
		setProgressSteps(getInitialProgressSteps());
		registrationMutation.mutate({ ...values, referralId: selectedReferralId });
	};

	const handleLoginSuccess = (affiliateData: any) => {
		setShowLoginModal(false);
		setAffiliateData(affiliateData);
		if (affiliateData?.referralId && affiliateData.referralId.length > 0) {
			setSelectedReferralId(affiliateData.referralId[0]);
			form.setFieldsValue({ referralId: affiliateData.referralId[0] });
		}
	};

	const handleBackToForm = () => {
		setCredentials(null);
		setShareLink('');
		setRegistrationStep('form');
		setProgressSteps(getInitialProgressSteps());
		form.resetFields();
		setSelectedReferralId(null);
	};

	if (credentials && shareLink) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden">
				<div className="absolute inset-0 overflow-hidden pointer-events-none">
					<div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-indigo-500/20 rounded-full blur-3xl" />
					<div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-purple-400/20 to-pink-500/20 rounded-full blur-3xl" />
				</div>

				<div className="absolute top-6 left-6 z-20">
					<Button
						type="text"
						icon={<ArrowLeftOutlined />}
						onClick={handleBackToForm}
						className="flex items-center gap-2 bg-white/80 backdrop-blur-sm hover:bg-white/90 border border-gray-200 hover:border-gray-300 rounded-xl px-4 py-2 text-gray-700 hover:text-gray-900 transition-all duration-200 shadow-lg hover:shadow-xl"
						size="large"
					>
						<span className="font-medium">Create Another</span>
					</Button>
				</div>

				<div className="min-h-screen flex w-full relative z-10">
					<div className="hidden lg:flex lg:w-1/2 flex-col justify-center relative">
						<div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-900" />
						<div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />

						<div className="relative z-10 max-w-lg mx-auto p-6">
							<div className="mb-8 text-center">
								<div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 mb-4">
									<span className="text-white text-xs font-medium">
										Trusted by 1,000+ businesses
									</span>
								</div>
								<h2 className="text-3xl font-bold text-white mb-3 leading-tight">
									<span className="bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
										Account Created Successfully!
									</span>
								</h2>
							</div>

							<div className="space-y-4" />
						</div>
					</div>

					<div className="w-full lg:w-1/2 flex items-center justify-center p-4">
						<div className="w-full max-w-md">
							<Card className="shadow-2xl rounded-3xl border-0 bg-white/80 backdrop-blur-sm overflow-hidden">
								<div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-500 via-blue-500 to-indigo-500" />

								<div className="p-6">
									<div className="text-center mb-6">
										<h2 className="text-2xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent mb-2">
											Account Created Successfully!
										</h2>
										<p className="text-gray-600 text-sm">Your affiliate account is ready to use</p>
									</div>

									<div className="mb-8">
										<h3 className="mb-4 text-gray-800 text-base font-semibold">
											🔐 Account Credentials:
										</h3>
										<Card size="small" className="bg-gray-50 mb-4 border border-gray-200">
											<p className="my-1">
												<strong>Username:</strong> {credentials.username}
											</p>
											<p className="my-1">
												<strong>Password:</strong> {credentials.password}
											</p>
										</Card>
									</div>

									<div className="mb-8">
										<h3 className="mb-4 text-gray-800 text-base font-semibold">
											📞 Contact Information:
										</h3>
										<Card size="small" className="bg-gray-50 mb-4 border border-gray-200">
											<p className="my-1">
												<strong>Email:</strong> {credentials.email}
											</p>
											{credentials.phone && (
												<p className="my-1">
													<strong>Phone:</strong> {credentials.phone}
												</p>
											)}
										</Card>
									</div>

									<div>
										<h3 className="mb-4 text-gray-800 text-base font-semibold">
											🔗 Shareable Link:
										</h3>
										<Card size="small" className="bg-gray-50 mb-4 border border-gray-200">
											<p className="break-all m-0 text-xs">{shareLink}</p>
										</Card>
										<Button
											type="primary"
											size="large"
											block
											onClick={() => {
												navigator.clipboard.writeText(shareLink);
												notification.success({ message: 'Link copied to clipboard!' });
											}}
											className="h-12 rounded-xl font-bold text-base bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 border-0 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
										>
											Copy Link
										</Button>
									</div>
								</div>
							</Card>
						</div>
					</div>
				</div>
			</div>
		);
	}

	if (registrationStep === 'processing') {
		return (
			<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden">
				<div className="absolute inset-0 overflow-hidden pointer-events-none">
					<div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-indigo-500/20 rounded-full blur-3xl" />
					<div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-purple-400/20 to-pink-500/20 rounded-full blur-3xl" />
				</div>

				<div className="min-h-screen flex w-full relative z-10">
					<div className="hidden lg:flex lg:w-1/2 flex-col justify-center relative">
						<div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-900" />
						<div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />

						<div className="relative z-10 max-w-lg mx-auto p-6 text-left">
							<div className="mb-8">
								<h1 className="text-4xl font-bold text-white mb-4">Frontdesk</h1>
								<h2 className="text-8xl font-black bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent mb-6">
									AFFILIATE PROGRAM
								</h2>
								<p className="text-2xl font-semibold text-blue-100">Create Shareable Agents</p>
							</div>
						</div>
					</div>

					<div className="w-full lg:w-1/2 flex items-center justify-center p-4">
						<div className="w-full max-w-md">
							<Card className="shadow-2xl rounded-3xl border-0 bg-white/80 backdrop-blur-sm overflow-hidden">
								<div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-500" />

								<div className="p-6 text-center">
									<div className="mb-6">
										<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4" />
										<h2 className="text-xl font-bold text-gray-800 mb-2">
											Setting Up Your Account
										</h2>
										<p className="text-gray-600 text-sm mb-4">
											Please wait while we create your affiliate account and configure your AI
											agent...
										</p>

										<SandboxerProgressIndicator progressSteps={progressSteps} />

										<div className="mt-4 text-xs text-gray-500">This may take up to 60 seconds</div>
									</div>
								</div>
							</Card>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<>
			<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden">
				<div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-20 md:block hidden">
					<div className="bg-white rounded-xl p-2 shadow-lg border border-white/20 backdrop-blur-sm">
						<div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-200 to-indigo-200 flex items-center justify-center">
							<img src={MOBILE_LOGO_S3_URL} alt="" className="w-6 h-6 rounded-lg" />
						</div>
					</div>
				</div>

				<div className="absolute inset-0 overflow-hidden pointer-events-none">
					<div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-indigo-500/20 rounded-full blur-3xl" />
					<div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-purple-400/20 to-pink-500/20 rounded-full blur-3xl" />
				</div>

				<div className="min-h-screen flex w-full relative z-10">
					<div className="hidden lg:flex lg:w-1/2 items-center justify-center relative overflow-hidden">
						<div className="absolute inset-0 z-0">
							<div className="w-full h-full bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-900" />
							<div className="w-full h-full bg-gradient-to-t from-black/30 to-transparent" />
						</div>

						<div className="relative z-10 max-w-2xl px-8 py-12 space-y-6 text-left">
							<h1 className="text-4xl font-bold text-white">Frontdesk</h1>
							<h2 className="text-7xl lg:text-8xl font-extrabold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent leading-tight tracking-tight drop-shadow-sm">
								AFFILIATE PROGRAM
							</h2>
							<p className="text-xl lg:text-2xl font-medium text-blue-100">
								Create Shareable Agent Demo
							</p>
						</div>
					</div>

					<div className="w-full lg:w-1/2 flex items-center justify-center p-4">
						<div className="w-full max-w-md">
							<div className="text-center mb-6">
								<h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-2">
									Affiliate Account Creation
								</h1>
								<p className="text-gray-600 text-sm mb-3">
									Create accounts and generate share links for your affiliates
								</p>
							</div>

							<Card className="shadow-2xl rounded-3xl border-0 bg-white/80 backdrop-blur-sm overflow-hidden">
								<div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-500" />

								<div className="p-6">
									<Form
										form={form}
										layout="vertical"
										onFinish={handleSubmit}
										requiredMark={false}
										className="space-y-4"
									>
										<Form.Item
											name="businessName"
											label={
												<span className="text-gray-700 font-semibold flex items-center gap-2 text-sm">
													Business Name *
												</span>
											}
											rules={[{ required: true, message: 'Please enter the business name' }]}
											className="mb-3"
										>
											<Input
												placeholder="e.g., Johnson's Auto Repair"
												className="h-12 rounded-xl border-2 border-gray-200 hover:border-blue-400 focus:border-blue-500 transition-all duration-200 bg-gray-50/50"
												size="large"
											/>
										</Form.Item>

										<Form.Item
											name="businessType"
											label={
												<span className="text-gray-700 font-semibold flex items-center gap-2 text-sm">
													Business Type *
												</span>
											}
											rules={[{ required: true, message: 'Please select the business type' }]}
											className="mb-3"
										>
											<Select
												options={businessTypes.map((type) => ({
													value: type.value || type.label,
													label: type.label,
												}))}
												placeholder="Select business type"
												size="large"
												className="rounded-xl business-type-select"
												onChange={(value) => setIsOtherBusinessType(value === 'Other')}
												style={{ height: '48px' }}
											/>
										</Form.Item>

										{isOtherBusinessType && (
											<Form.Item
												name="otherBusinessType"
												label={
													<span className="text-gray-700 font-semibold flex items-center gap-2 text-sm">
														Specify Business Type *
													</span>
												}
												rules={[{ required: true, message: 'Please specify the business type' }]}
												className="mb-3"
											>
												<Input
													placeholder="e.g., Pet Grooming, Consulting"
													className="h-12 rounded-xl border-2 border-gray-200 hover:border-purple-400 focus:border-purple-500 transition-all duration-200 bg-gray-50/50"
													size="large"
												/>
											</Form.Item>
										)}

										<Form.Item
											name="emailAddress"
											label={
												<span className="text-gray-700 font-semibold flex items-center gap-2 text-sm">
													Email Address *
												</span>
											}
											rules={[
												{ required: true, message: 'Please enter the email address' },
												{ type: 'email', message: 'Please enter a valid email address' },
											]}
											className="mb-3"
										>
											<Input
												placeholder="e.g., contact@business.com"
												className="h-12 rounded-xl border-2 border-gray-200 hover:border-blue-400 focus:border-blue-500 transition-all duration-200 bg-gray-50/50"
												size="large"
											/>
										</Form.Item>

										<Form.Item
											name="phoneNumber"
											label={
												<span className="text-gray-700 font-semibold flex items-center gap-2 text-sm">
													Phone Number (Optional)
												</span>
											}
											className="mb-3"
										>
											<Input
												placeholder="e.g., +1 (555) 123-4567"
												className="h-12 rounded-xl border-2 border-gray-200 hover:border-green-400 focus:border-green-500 transition-all duration-200 bg-gray-50/50"
												size="large"
											/>
										</Form.Item>

										<Form.Item
											name="businessWebsite"
											label={
												<span className="text-gray-700 font-semibold flex items-center gap-2 text-sm">
													Website (Optional)
												</span>
											}
											rules={[
												{
													validator: (_, value) => {
														if (!value || !value.trim()) {
															return Promise.resolve();
														}

														const fullUrl = `https://${value.trim()}`;
														const urlPattern =
															/^https?:\/\/[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*([\/][^\s]*)?$/;
														if (!urlPattern.test(fullUrl)) {
															return Promise.reject(
																new Error('Please enter a valid domain (e.g., example.com)'),
															);
														}
														return Promise.resolve();
													},
												},
											]}
											className="mb-4"
										>
											<Input
												addonBefore={<span className="text-gray-600 font-medium">https://</span>}
												placeholder="example.com"
												className="rounded-xl border-none bg-gray-50/50"
												size="large"
												onChange={(e) => {
													let value = e.target.value;

													if (value.startsWith('https://')) {
														value = value.substring(8);
													} else if (value.startsWith('http://')) {
														value = value.substring(7);
													}

													form.setFieldsValue({ businessWebsite: value });
												}}
											/>
										</Form.Item>

										{affiliateData?.referralId && affiliateData.referralId.length > 0 ? (
											<Form.Item
												name="referralId"
												label={
													<span className="text-gray-700 font-semibold flex items-center gap-2 text-sm">
														Referral ID *
													</span>
												}
												rules={[{ required: true, message: 'Please select a referral ID' }]}
												className="mb-4"
											>
												<Select
													options={affiliateData.referralId.map((id: string) => ({
														value: id,
														label: `${id}`,
													}))}
													placeholder="Select referral ID"
													size="large"
													className="rounded-xl referral-id-select"
													value={selectedReferralId}
													onChange={(value) => {
														setSelectedReferralId(value);
														form.setFieldsValue({ referralId: value });
													}}
													style={{ height: '48px' }}
												/>
											</Form.Item>
										) : (
											<Form.Item
												label={
													<span className="text-gray-700 font-semibold flex items-center gap-2 text-sm">
														Referral ID
													</span>
												}
												className="mb-4"
											>
												<div className="h-12 rounded-xl border-2 border-gray-200 bg-gray-50/50 flex items-center px-3 text-gray-500 text-sm">
													You need to generate referral link first
												</div>
											</Form.Item>
										)}

										<Form.Item className="mb-0">
											<Button
												type="primary"
												htmlType="submit"
												className="w-full h-12 rounded-xl font-bold text-base bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 border-0 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
												loading={registrationMutation.isPending}
												disabled={
													!affiliateData?.referralId ||
													affiliateData.referralId.length === 0 ||
													!selectedReferralId ||
													dailyCountCheckQuery.data?.count >= 10
												}
											>
												<span className="flex items-center justify-center gap-2">
													🚀 Create Affiliate Account
												</span>
											</Button>
										</Form.Item>
									</Form>

									<div className="mt-4 pt-4 border-t border-gray-100">
										<div className="space-y-2 text-xs text-gray-500">
											<div className="flex items-center justify-center gap-4">
												<div className="flex items-center gap-1">
													<CheckCircleFilled className="text-green-500 text-xs" />
													<span>Auto-generated credentials</span>
												</div>
												<div className="flex items-center gap-1">
													<CheckCircleFilled className="text-green-500 text-xs" />
													<span>Instant share link</span>
												</div>
											</div>
											<div className="flex items-center justify-center gap-4">
												<div className="flex items-center gap-1">
													<CheckCircleFilled className="text-green-500 text-xs" />
													<span>Ready to use immediately</span>
												</div>
											</div>
										</div>
									</div>

									<div className="mt-4 pt-4 border-t border-gray-100">
										<Button
											type="default"
											onClick={() => setShowLoginModal(true)}
											className="w-full h-10 rounded-lg border-gray-300 text-gray-700 hover:border-blue-400 hover:text-blue-600"
										>
											🔐 Login to another account
										</Button>
									</div>
								</div>
							</Card>
						</div>
					</div>
				</div>
			</div>

			<LoginModal visible={showLoginModal} onLoginSuccess={handleLoginSuccess} />
		</>
	);
};

export default AffiliateRegisterPage;
